import { useQuery } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import { getFullName } from "../../../../shared/utils/get-full-name";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";

/**
 * What the patients list can do for the AI omnibar.
 *
 * `scope: "page"` because these only work while this page is on screen - `app.navigate` declares
 * that it opens that space, so "active patients in Brooklyn" comes back as navigate + filter rather
 * than navigate and nothing.
 */

const PATIENT_STATUSES = [
  "ACTIVE",
  "ACCEPTED",
  "ELIGIBLE",
  "REFERRAL",
  "DRAFT",
  "ON_HOLD",
  "HOSPITALIZED",
  "VACATION",
  "DISCHARGED",
  "DECEASED",
  "LOST",
  "PENDING_FILES",
] as const;

function toStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

declareAiCapability({
  id: "patients.filter",
  title: "Filter the patients list",
  effect: "prefill",
  description:
    "Sets the search box and the status filter on the patients list, so the table shows the patients being asked about. Only works while the patients list is open.",
  location: "the filter bar of the patients list",
  scope: "page",
  permissionKey: "view_patients_section_list",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      description:
        "Free text over the table, for what no other filter can hold: a person's name, a admission id, a street, a phone number. An office, contract, team or coordinator name belongs in its own filter, not here.",
      // Where a word that is in none of this screen's lists ends up - "brooklyn", a street, a
      // surname - rather than the step failing.
      searchesFreeText: true,
    },
    {
      name: "status",
      type: "enum",
      required: false,
      description: "Show only patients with this status.",
      options: PATIENT_STATUSES.map((status) => ({
        value: status,
        label: toStatusLabel(status),
      })),
    },
    // The four below are the rest of this page's filter bar. Their options are agency data the page
    // already loads, contributed by the hook below rather than declared here.
    {
      name: "office",
      type: "enum",
      required: false,
      description: "Show only patients of one office.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      description: "Show only patients on one contract.",
    },
    {
      name: "team",
      type: "enum",
      required: false,
      description: "Show only patients of one team.",
    },
    {
      name: "coordinator",
      type: "enum",
      required: false,
      description: "Show only patients assigned to one coordinator.",
    },
  ],
  examples: [
    "show me discharged patients",
    "find patients on hold in the list",
    "active patients in the Brooklyn office",
  ],
});

declareAiCapability({
  id: "patients.columns",
  title: "Show or hide a column on the patients list",
  effect: "prefill",
  description:
    "Turns one of the patients list's columns on or off. The choice sticks for this user, the same as using the Columns control.",
  location: "the Columns control of the patients list",
  scope: "page",
  permissionKey: "view_patients_section_list",
  params: [
    {
      name: "field",
      type: "enum",
      required: true,
      multiple: true,
      description:
        "Which columns to change. Several at once are a selection rather than a conflict: asking for the medicaid number and member id columns turns both on in one step.",
      // Contributed by the mounted table, because the column set is the table's own business.
    },
    {
      name: "show",
      type: "boolean",
      required: false,
      description: "True to show the column, false to hide it. Defaults to showing it.",
    },
  ],
  examples: ["show the medicaid number column", "hide the created at column"],
});

/**
 * Publishes an `{ id, name }` list as a param's options for as long as this page is mounted.
 *
 * Contributed rather than declared because these are agency records the page loads: they do not
 * exist when the module is imported, and they should disappear from the manifest with the page.
 */
function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("patients.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

/**
 * An option value back in the type the query wants.
 *
 * Everything crosses the omnibar's wire as a string, but these filter variables are typed - offices,
 * teams and coordinators are id scalars - and a "2" where a 2 belongs is what makes the GraphQL call
 * fail. See the same note on the caregivers list.
 */
export function toIdValue(value: string): string | number {
  return /^\d+$/.test(value) ? Number(value) : value;
}

/** The label the Columns control shows for a column, falling back to its id. */
function getColumnLabel(column: { id: string; columnDef: { header?: unknown } }): string {
  const header = column.columnDef.header;

  return typeof header === "string" && header !== "" ? header : column.id;
}

/**
 * One setter per filter this capability declares.
 *
 * The patients list exists twice - the endpoint list and the GraphQL one it replaced, chosen by a
 * beta switch - and they do not agree on a single filter's name or shape: an office is `officeId:
 * [id]` in one and `offices: { in: [id] }` in the other. So each list passes its own mapping and
 * this file stays out of it, rather than carrying a table of both and going stale when one changes.
 */
export type PatientsDashboardAiFilterSetters = {
  search: (value: string) => void;
  status: (value: string) => void;
  office: (value: string) => void;
  contract: (value: string) => void;
  team: (value: string) => void;
  coordinator: (value: string) => void;
};

export function usePatientsDashboardAiCapabilities<TData>(params: {
  table: Table<TData>;
  setFilter: PatientsDashboardAiFilterSetters;
}): void {
  const { table, setFilter } = params;
  const { queries } = useApi();

  // The same queries the filter bar's own selects use, so this reads their cache rather than
  // fetching anything of its own.
  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());
  const teams = useQuery(queries.agencyTeam.list());
  const agencyMembers = useQuery(queries.agencyMember.list());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);
  useContributedOptions("team", teams.data?.agencyTeams);
  useContributedOptions(
    "coordinator",
    React.useMemo(
      () =>
        (agencyMembers.data?.agencyMembers ?? [])
          .filter((member) =>
            ["Coordinator", "SeniorCoordinator", "HeadOfCoordination"].includes(member.jobTitle)
          )
          .map((member) => ({ id: member.id, name: getFullName(member) })),
      [agencyMembers.data?.agencyMembers]
    )
  );

  const tableRef = React.useRef(table);
  tableRef.current = table;

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  const columns = table.getAllLeafColumns().filter((column) => column.getCanHide());
  const columnsKey = columns.map((column) => column.id).join("|");

  React.useEffect(() => {
    const releases = tableRef.current
      .getAllLeafColumns()
      .filter((column) => column.getCanHide())
      .map((column) =>
        contributeAiCapabilityParamOption("patients.columns", "field", {
          value: column.id,
          label: getColumnLabel(column),
        })
      );

    return () => releases.forEach((release) => release());
  }, [columnsKey]);

  // Not available until the filter bar's own lists have arrived. Otherwise the step runs a moment
  // after this page mounts, the office and contract options are still empty, and a word like
  // "europe" that belongs to a real filter falls through to the search box instead.
  const listsLoaded =
    offices.isSuccess && contracts.isSuccess && teams.isSuccess && agencyMembers.isSuccess;

  useProvideAiCapability(
    "patients.filter",
    (args, context) => {
      const applied: string[] = [];
      // The label the option carried, so the line says "Brooklyn" rather than an id.
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.search !== undefined) {
        setFilterRef.current.search(String(args.search));
        applied.push(`"${String(args.search)}"`);
      }

      if (args.status !== undefined) {
        setFilterRef.current.status(String(args.status));
        applied.push(toStatusLabel(String(args.status)));
      }

      for (const name of ["office", "contract", "team", "coordinator"] as const) {
        if (args[name] === undefined) {
          continue;
        }

        setFilterRef.current[name](String(args[name]));
        applied.push(label(name));
      }

      if (applied.length === 0) {
        throw new Error("Tell me what to filter the list by.");
      }

      return `Showing patients: ${applied.join(", ")}`;
    },
    { enabled: listsLoaded }
  );

  useProvideAiCapability("patients.columns", (args) => {
    const wanted = Array.isArray(args.field) ? args.field : [args.field];
    const columns = wanted.map((one) => {
      const column = tableRef.current
        .getAllLeafColumns()
        .find((candidate) => candidate.id === String(one));

      if (column === undefined) {
        throw new Error(`The patients list has no "${String(one)}" column.`);
      }

      return column;
    });

    const shouldShow = args.show !== false;

    // Checked before anything is toggled: half a selection applied is worse than none, because the
    // person cannot tell which half.
    columns.forEach((column) => column.toggleVisibility(shouldShow));

    const names = columns.map((column) => getColumnLabel(column)).join(" and ");

    return `${shouldShow ? "Showing" : "Hid"} the ${names} column${
      columns.length === 1 ? "" : "s"
    }`;
  });
}
